/**
 * Constantes de format et de séparation de domaine du cœur zéro-knowledge Klavox.
 *
 * ⚠️ CONTRAT DE FORMAT — toute modification d'une valeur de ce fichier rend
 * illisibles les coffres déjà chiffrés. Un changement exige une NOUVELLE version
 * de format (`SEALED_BLOB_VERSION`) et un chemin de migration explicite.
 *
 * Voir `docs/ARCHITECTURE.md` §1 et §2.
 */

/** Identifiant du contrat crypto implémenté par ce paquet. */
export const CRYPTO_CORE_CONTRACT = 'zero-knowledge/v1';

/** Version du conteneur `version | algo | nonce | ciphertext | tag`. */
export const SEALED_BLOB_VERSION = 1;

/**
 * Algorithmes AEAD autorisés. Le défaut est XChaCha20-Poly1305 : nonce de
 * 192 bits, donc un nonce purement aléatoire est sûr sans compteur (probabilité
 * de collision négligeable), et implémentation en temps constant en JS pur —
 * contrairement à AES logiciel.
 *
 * ⚠️ AES-256-GCM n'a qu'un nonce de 96 bits. Avec des nonces ALÉATOIRES, la
 * borne des anniversaires impose de rester très en deçà de 2^32 écritures sous
 * une même clé (~4 milliards ; au-delà, une collision de nonce devient
 * probable, et une collision détruit la confidentialité ET permet la forge).
 * Un coffre réel n'approche jamais cet ordre de grandeur, mais c'est la raison
 * pour laquelle AES-256-GCM reste une ALTERNATIVE (plateformes à AES matériel)
 * et non le défaut : XChaCha20-Poly1305 n'a pas cette limite.
 */
export const AEAD_ALGORITHM = {
  xchacha20poly1305: 1,
  aes256gcm: 2,
} as const;

export type AeadAlgorithmId = (typeof AEAD_ALGORITHM)[keyof typeof AEAD_ALGORITHM];

/** Algorithme utilisé par défaut à l'écriture. Les deux restent lisibles. */
export const DEFAULT_AEAD_ALGORITHM: AeadAlgorithmId = AEAD_ALGORITHM.xchacha20poly1305;

/** Noms lisibles (journalisation d'algorithme uniquement — jamais de secret). */
export const AEAD_ALGORITHM_NAMES: Readonly<Record<AeadAlgorithmId, string>> = Object.freeze({
  [AEAD_ALGORITHM.xchacha20poly1305]: 'XChaCha20-Poly1305',
  [AEAD_ALGORITHM.aes256gcm]: 'AES-256-GCM',
});

/** Longueur de nonce imposée par algorithme (en octets). */
export const NONCE_LENGTH: Readonly<Record<AeadAlgorithmId, number>> = Object.freeze({
  [AEAD_ALGORITHM.xchacha20poly1305]: 24,
  [AEAD_ALGORITHM.aes256gcm]: 12,
});

/** Longueur du tag d'authentification Poly1305 / GHASH (en octets). */
export const TAG_LENGTH = 16;

/** Toutes les clés symétriques Klavox sont de 256 bits. */
export const SYMMETRIC_KEY_LENGTH = 32;

/** `masterPasswordHash` : 256 bits dérivés par HKDF-SHA256. */
export const MASTER_PASSWORD_HASH_LENGTH = 32;

/**
 * Clé de récupération : 128 bits (`docs/ARCHITECTURE.md` §4), générée par le
 * CSPRNG. Entropie déjà suffisante pour servir de clé de scellement une fois
 * étirée par HKDF : contrairement au mot de passe maître, elle n'a pas besoin
 * d'Argon2id.
 */
export const RECOVERY_KEY_LENGTH = 16;

/**
 * Sortie de l'extension WebAuthn `prf` : 32 octets, fixés par la spécification
 * (`docs/ARCHITECTURE.md` §4). C'est un SECRET — l'authentificateur est le seul
 * à pouvoir le reproduire, et seulement après vérification de l'utilisateur
 * (biométrie ou PIN).
 */
export const PASSKEY_PRF_OUTPUT_LENGTH = 32;

/**
 * Sel passé à l'extension `prf` (`eval.first`). Non secret : il est stocké en
 * clair à côté du blob scellé. Un sel par passkey enrôlée, pour que deux
 * appareils n'évaluent jamais la même valeur.
 */
export const PASSKEY_PRF_SALT_LENGTH = 32;

/** En-tête du conteneur scellé : `version` (1 o) + `algo` (1 o). */
export const SEALED_HEADER_LENGTH = 2;

/**
 * Klavox Send (`R15`) : secret de 256 bits tiré du CSPRNG, transporté dans le
 * FRAGMENT `#` de l'URL de partage — donc jamais envoyé au serveur, même par
 * le navigateur du destinataire (`docs/ARCHITECTURE.md` §2).
 */
export const SEND_SECRET_LENGTH = 32;

/**
 * Sel public d'un envoi, stocké en clair à côté du ciphertext. Il rend deux
 * envois du même contenu, avec le même mot de passe, cryptographiquement
 * indépendants.
 */
export const SEND_SALT_LENGTH = 16;

/**
 * Accès d'urgence (`R17`, `docs/EMERGENCY-ACCESS-DESIGN.md`) : secret du KIT
 * remis HORS LIGNE au contact de confiance (imprimé, en main propre). 256 bits
 * du CSPRNG — il ne transite jamais par le serveur, qui ne voit qu'une preuve
 * dérivée à sens unique.
 */
export const EMERGENCY_KIT_SECRET_LENGTH = 32;

/**
 * Jeton de service (`R20`, CLI `klavox run`) : secret porteur remis a un
 * processus automatise. 256 bits du CSPRNG — assez d'entropie pour se passer
 * d'Argon2id (rien a deviner), contrairement a un mot de passe humain.
 */
export const SERVICE_TOKEN_SECRET_LENGTH = 32;

/**
 * Chaînes de séparation de domaine de la dérivation de clés.
 * Elles garantissent qu'une clé dérivée pour un usage ne peut servir à un autre.
 */
export const KDF_DOMAIN = Object.freeze({
  /** Préfixe du sel Argon2id (haché avec l'e-mail normalisé). */
  salt: 'klavox|zero-knowledge/v1|argon2id-salt',
  /** `info` HKDF de la clé de chiffrement. */
  stretchedMasterKey: 'klavox|zero-knowledge/v1|stretched-master-key',
  /** `info` HKDF du matériel d'authentification serveur. */
  masterPasswordHash: 'klavox|zero-knowledge/v1|master-password-hash',
  /** `info` HKDF de la clé d'étirement de la clé de récupération. */
  recoveryStretchedKey: 'klavox|zero-knowledge/v1|recovery-stretched-key',
  /** `info` HKDF de la preuve de possession de la clé de récupération, que le serveur exige pour réinitialiser. */
  recoveryAuthProof: 'klavox|zero-knowledge/v1|recovery-auth-proof',
  /** `info` HKDF de la clé d'étirement de la sortie PRF d'une passkey. */
  passkeyStretchedKey: 'klavox|zero-knowledge/v1|passkey-stretched-key',
  /** `info` HKDF de la clé d'un envoi Klavox Send (secret d'URL + mot de passe éventuel). */
  sendKey: 'klavox|zero-knowledge/v1|send-key',
  /** Préfixe du sel Argon2id du mot de passe OPTIONNEL d'un envoi Klavox Send. */
  sendPasswordSalt: 'klavox|zero-knowledge/v1|send-password-salt',
  /** `info` HKDF de la clé de scellement de la `vaultKey` par un kit d'accès d'urgence. */
  emergencySealingKey: 'klavox|zero-knowledge/v1|emergency-sealing-key',
  /**
   * `info` HKDF de la preuve d'accès d'urgence — la SEULE dérivée du kit que
   * le serveur voit. Domaine distinct : la preuve ne permet pas de retrouver
   * la clé de scellement.
   */
  emergencyAccessProof: 'klavox|zero-knowledge/v1|emergency-access-proof',
  /** `info` HKDF de la clé de scellement d'un jeton de service (CLI `klavox run`). */
  serviceTokenKey: 'klavox|zero-knowledge/v1|service-token-key',
  /**
   * `info` HKDF de la preuve d'accès d'un jeton au registre SERVEUR. Domaine
   * distinct de `serviceTokenKey` : le serveur voit la preuve, il n'en tire
   * pas la clé de scellement.
   */
  serviceTokenAccessProof: 'klavox|zero-knowledge/v1|service-token-access-proof',
  /** Préfixe haché avec la preuve d'un jeton pour obtenir le vérificateur stocké côté serveur. */
  serviceTokenAccessVerifier: 'klavox|zero-knowledge/v1|service-token-access-verifier',
  /** Préfixe haché avec la preuve pour obtenir le vérificateur stocké côté serveur. */
  emergencyAccessVerifier: 'klavox|zero-knowledge/v1|emergency-access-verifier',
});

/**
 * Contextes AEAD. Liés en données associées (AAD), ils empêchent de rejouer un
 * blob d'un usage dans un autre (ex. présenter une entrée chiffrée à la place
 * de la protected symmetric key).
 */
export const SEAL_CONTEXT = Object.freeze({
  protectedSymmetricKey: 'klavox|zero-knowledge/v1|protected-symmetric-key',
  vaultEntry: 'klavox|zero-knowledge/v1|vault-entry',
  /** `vaultKey` scellée sous la clé de récupération plutôt que sous le mot de passe maître. */
  recoveryProtectedSymmetricKey: 'klavox|zero-knowledge/v1|recovery-protected-symmetric-key',
  /** `vaultKey` scellée sous la sortie PRF d'une passkey (raccourci LOCAL de déverrouillage). */
  passkeyProtectedSymmetricKey: 'klavox|zero-knowledge/v1|passkey-protected-symmetric-key',
  /**
   * Contenu d'un envoi Klavox Send. Contexte DISTINCT de `vaultEntry` : un
   * blob d'envoi ne peut pas être présenté comme une entrée de coffre, et
   * réciproquement — même si les deux clés venaient à coïncider.
   */
  send: 'klavox|zero-knowledge/v1|send',
  /** `vaultKey` scellée sous un kit d'accès d'urgence (contact de confiance, `R17`). */
  emergencyProtectedSymmetricKey: 'klavox|zero-knowledge/v1|emergency-protected-symmetric-key',
  /**
   * Valeurs portées par un jeton de service (`R20`). Contexte DISTINCT :
   * un jeton scopé ne peut pas être présenté comme une entrée de coffre, donc
   * il ne donne accès qu'à ce qui a été scellé POUR lui — la portée est
   * cryptographique, pas une règle d'autorisation qu'un serveur pourrait mentir.
   */
  serviceToken: 'klavox|zero-knowledge/v1|service-token',
});

/** Seul KDF de mot de passe maître autorisé. */
export type KdfAlgorithm = 'argon2id';

/**
 * Paramètres du KDF. Ils sont stockés en clair côté serveur (métadonnée non
 * secrète) pour qu'un nouvel appareil puisse rejouer la dérivation.
 */
export interface KdfParams {
  readonly algorithm: KdfAlgorithm;
  /** Mémoire Argon2id en KiB. */
  readonly memoryKiB: number;
  /** Nombre de passes Argon2id (`t`). */
  readonly iterations: number;
  /** Parallélisme Argon2id (`p`). */
  readonly parallelism: number;
  /**
   * Sel Argon2id ALÉATOIRE du compte ({@link KDF_SALT_LENGTH} octets du CSPRNG,
   * base64). Présent : c'est le sel, l'e-mail n'entre plus dans la dérivation
   * (coffre créé sans e-mail). Absent : sel dérivé de l'e-mail (comptes créés
   * avant, `deriveKdfSalt`). Non secret, comme les autres paramètres.
   */
  readonly saltBase64?: string;
}

/** Longueur du sel Argon2id aléatoire d'un compte créé sans e-mail (256 bits). */
export const KDF_SALT_LENGTH = 32;

/**
 * Défaut = seconde option recommandée par la RFC 9106 §4 :
 * Argon2id, t=3, p=4, m=64 MiB. Conforme au plancher de `CLAUDE.md`.
 */
export const DEFAULT_KDF_PARAMS: KdfParams = Object.freeze({
  algorithm: 'argon2id',
  memoryKiB: 65536,
  iterations: 3,
  parallelism: 4,
});

/**
 * Plancher de sécurité NON NÉGOCIABLE (`CLAUDE.md`) : mémoire ≥ 64 Mo,
 * itérations ≥ 3, parallélisme 1-4. Des paramètres plus faibles sont refusés.
 */
export const KDF_POLICY = Object.freeze({
  minMemoryKiB: 65536,
  maxMemoryKiB: 4194304,
  minIterations: 3,
  maxIterations: 64,
  minParallelism: 1,
  maxParallelism: 4,
});
